import { TColumnName, TRow } from "../types";
import {
  CON_SCORE_COLUMN_PREFIX,
  META_COLUMN_PREFIX,
  METHOD_COLUMN_PREFIX,
  METHOD_COLUMN_SUFFIX_X,
  METHOD_COLUMN_SUFFIX_Y,
  METHOD_COLUMN_SUFFIX_Z,
  PREDICTION_COLUMN_PREFIX,
} from "../constants";
import RowUtils from "./RowUtils";
import ColumnNameUtils from "./ColumnNameUtils";
import MathUtils from "./MathUtils";

class ColumnUtils {
  static isContinuous(
    column: TColumnName,
    continuousColumns: TColumnName[],
    continuousPredictionColumns: TColumnName[]
  ) {
    return (
      continuousColumns.includes(column) ||
      continuousPredictionColumns.includes(column)
    );
  }

  static isPredictionColumn(
    column: TColumnName,
    categoricalPredictionColumns: TColumnName[],
    continuousPredictionColumns: TColumnName[]
  ) {
    return (
      categoricalPredictionColumns.includes(column) ||
      continuousPredictionColumns.includes(column)
    );
  }

  static combine(
    {
      continuousColumns,
      categoricalColumns,
      categoricalPredictionColumns,
      continuousPredictionColumns,
      selectedMethod,
    }: {
      categoricalPredictionColumns: TColumnName[];
      continuousPredictionColumns: TColumnName[];
      categoricalColumns: TColumnName[];
      continuousColumns: TColumnName[];
      selectedMethod?: TColumnName;
      sort?: boolean;
    },
    sort?: boolean
  ) {
    let predictionColumns = [
      ...categoricalPredictionColumns,
      ...continuousPredictionColumns,
    ];

    if (selectedMethod) {
      predictionColumns = predictionColumns.filter((column) =>
        ColumnNameUtils.isPredictionForMethod(column, selectedMethod)
      );
    }

    /**
     * order is important
     */
    const allColumns = [
      ...predictionColumns,
      ...categoricalColumns,
      ...continuousColumns,
    ];
    return sort ? allColumns.sort() : allColumns;
  }

  static getColumnValue(
    row: TRow,
    column: TColumnName,
    columnType?: "prediction" | "meta"
  ) {
    let columnName = column;

    switch (columnType) {
      case "prediction":
        columnName = column;
        break;
      case "meta":
        columnName = column;
        break;

      default:
        break;
    }

    return row[columnName];
  }

  static getMetaLabel(column: TColumnName, method: TColumnName) {
    return column.replace(method + "_", "");
  }

  static getPredictionColumnsForMethod(
    columns: TColumnName[],
    method: TColumnName
  ) {
    return columns.filter((column) =>
      ColumnNameUtils.isPredictionForMethod(column, method)
    );
  }

  static getMethodColumnYZNames(methodXColumnName: TColumnName) {
    const methodWithoutSuffix = methodXColumnName.replace(
      METHOD_COLUMN_SUFFIX_X,
      ""
    );

    return {
      Y: (methodWithoutSuffix + METHOD_COLUMN_SUFFIX_Y) as TColumnName,
      Z: (methodWithoutSuffix + METHOD_COLUMN_SUFFIX_Z) as TColumnName,
    };
  }

  static getDefaultColumn({
    categoricalPredictionColumns,
    continuousPredictionColumns,
    categoricalColumns,
    selectedMethod,
  }: {
    categoricalColumns: TColumnName[];
    categoricalPredictionColumns: TColumnName[];
    continuousPredictionColumns: TColumnName[];
    selectedMethod: TColumnName;
  }) {
    const filteredCategoricalPredictionColumns =
      ColumnUtils.getPredictionColumnsForMethod(
        categoricalPredictionColumns,
        selectedMethod
      );
    const filteredContinuousPredictionColumns =
      ColumnUtils.getPredictionColumnsForMethod(
        continuousPredictionColumns,
        selectedMethod
      );

    if (filteredCategoricalPredictionColumns.length > 0) {
      return filteredCategoricalPredictionColumns[0];
    } else if (filteredContinuousPredictionColumns.length > 0) {
      return filteredContinuousPredictionColumns[0];
    }

    return categoricalColumns[0];
  }

  static findColumns(firstRow: Record<string, any>) {
    const columns = RowUtils.getColumnNames(firstRow);
    const columLabelToName: Record<string, TColumnName> = {};

    let methodColumns: TColumnName[] = [];
    let metaColumns: TColumnName[] = [];
    const continuousColumns: TColumnName[] = [];
    const categoricalColumns: TColumnName[] = [];
    const continuousPredictionColumns: TColumnName[] = [];
    const categoricalPredictionColumns: TColumnName[] = [];

    columns.forEach((column) => {
      if (column.startsWith(CON_SCORE_COLUMN_PREFIX)) {
        return;
      }

      if (column.startsWith(METHOD_COLUMN_PREFIX)) {
        if (column.endsWith(METHOD_COLUMN_SUFFIX_X)) {
          methodColumns.push(column);
        }
      } else if (column.startsWith(META_COLUMN_PREFIX)) {
        metaColumns.push(column);
      } else if (column.includes(PREDICTION_COLUMN_PREFIX)) {
        const value = firstRow[column];

        if (MathUtils.isNumberAsString(value)) {
          continuousPredictionColumns.push(column);
        } else {
          categoricalPredictionColumns.push(column);
        }
      } else {
        // method columns have no prefixes

        // TODO to keep this?
        // skip ID columns
        if (column.includes("ID")) {
          return;
        }

        const value = firstRow[column];

        if (value === undefined) {
          console.error(`value is undefined for column ${column}`);
          return;
        }

        if (MathUtils.isNumberAsString(value)) {
          continuousColumns.push(column);
        } else {
          categoricalColumns.push(column);
        }
      }
    });

    const validMethodColumns = methodColumns.filter((column) => {
      const { Y: methodYName, Z: methodZName } =
        ColumnUtils.getMethodColumnYZNames(column);
      return columns.includes(methodYName) && columns.includes(methodZName);
    });

    return {
      methodColumns: validMethodColumns.sort(),
      continuousColumns: continuousColumns.sort(),
      categoricalColumns: categoricalColumns.sort(),
      continuousPredictionColumns: continuousPredictionColumns.sort(),
      categoricalPredictionColumns: categoricalPredictionColumns.sort(),
      metaColumns,
      columLabelNameMapping: columLabelToName,
    };
  }
}

export default ColumnUtils;
